import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, ChevronRight, Loader, X } from 'lucide-react';

/**
 * Choisir une catégorie dans la taxonomie Google.
 *
 * ## Pourquoi un composant, et pas un champ
 *
 * La taxonomie compte **environ 5 595 entrées**, et Google n'accepte qu'un **identifiant
 * numérique** — `780`, pas « Vêtements > Chemises ». Un champ libre produirait des valeurs que
 * Merchant Center refuse, et une liste déroulante de 5 595 lignes ne se parcourt pas.
 *
 * D'où deux modes, servis par la même route (`/api/tools/feed-optimizer/google-taxonomy`) :
 *
 * | Mode | Quand | Ce que l'API reçoit |
 * |---|---|---|
 * | recherche | dès 2 caractères saisis | `?search=&limit=50` |
 * | navigation | sinon | `?parent=` ou rien, pour les 21 racines |
 *
 * ⚠️ **Les deux s'excluent** : entrer dans un niveau efface la recherche, et saisir un terme
 * quitte l'arborescence. Les mêler afficherait une liste dont on ne saurait plus d'où elle vient.
 *
 * ⚠️ **La recherche est plafonnée à 50 résultats** par l'API, qui le dit (`truncated`). Le taire
 * laisserait croire qu'une catégorie n'existe pas alors qu'elle est au 51ᵉ rang.
 *
 * ## Une seconde implémentation, et c'est provisoire
 *
 * `FeedOptimizerTool.jsx` porte la sienne (son `rendreChoixCategorie`), tissée dans l'état d'un
 * composant de 3 551 lignes. Ce module existe pour servir la correction lancée depuis Radar sans
 * toucher à l'outil ; l'étape suivante est que l'outil l'adopte et supprime sa copie. En
 * attendant, **toute évolution de la taxonomie se fait des deux côtés**.
 */
const ChoixCategorieGoogle = ({ token, valeur, nomValeur, onChoisir, onEffacer }) => {
  const [terme, setTerme] = useState('');
  const [chemin, setChemin] = useState(null);
  const [resultats, setResultats] = useState([]);
  const [tronque, setTronque] = useState(false);
  const [chargement, setChargement] = useState(false);
  const [ouvert, setOuvert] = useState(false);
  const boite = useRef(null);

  const chercher = useCallback(async (recherche, parent) => {
    const avecTerme = recherche && recherche.length >= 2;
    setChargement(true);
    try {
      const parametres = new URLSearchParams(avecTerme
        ? { search: recherche, limit: '50' }
        : (parent ? { parent } : {}));
      const reponse = await fetch(`/api/tools/feed-optimizer/google-taxonomy?${parametres}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const donnees = await reponse.json();
      if (!reponse.ok || !donnees.success) throw new Error(donnees.error || 'Taxonomie indisponible');
      setResultats(donnees.categories || []);
      setTronque(Boolean(donnees.truncated));
    } catch (e) {
      /*
       * ⚠️ La taxonomie est téléchargée chez Google et mise en cache par processus : elle peut
       * manquer après un redéploiement, ou si google.com est injoignable. On vide la liste et on
       * le dit, plutôt que de laisser un sélecteur muet qu'on croirait vide.
       */
      setResultats([]);
      setTronque(false);
    } finally {
      setChargement(false);
    }
  }, [token]);

  useEffect(() => {
    if (!ouvert) return undefined;
    const avecTerme = terme && terme.length >= 2;
    // La frappe est amortie ; la navigation, elle, est immédiate — on vient de cliquer.
    const minuteur = setTimeout(() => chercher(terme, chemin), avecTerme ? 300 : 0);
    return () => clearTimeout(minuteur);
  }, [terme, chemin, ouvert, chercher]);

  /** Fermer au clic extérieur : une liste ouverte par-dessus le reste piège l'écran. */
  useEffect(() => {
    const auClic = (e) => { if (boite.current && !boite.current.contains(e.target)) setOuvert(false); };
    document.addEventListener('mousedown', auClic);
    return () => document.removeEventListener('mousedown', auClic);
  }, []);

  const retenir = (categorie) => {
    onChoisir(categorie);
    setOuvert(false);
    setTerme('');
    setChemin(null);
  };

  // Le fil d'Ariane, reconstruit depuis le chemin courant — « A > B > C » donne trois niveaux.
  const niveaux = chemin ? chemin.split(' > ') : [];

  return (
    <div className="kcat" ref={boite}>
      {valeur ? (
        <div className="kcat-retenue">
          <span className="kcat-nom">{nomValeur || `Catégorie ${valeur}`}</span>
          <span className="kcat-id">{valeur}</span>
          <button type="button" className="kcorr-retirer" onClick={onEffacer} aria-label="Effacer la catégorie">
            <X size={14} />
          </button>
        </div>
      ) : (
        <div className="kcat-champ">
          <Search size={14} />
          <input
            type="text"
            value={terme}
            placeholder="Chercher une catégorie, ou parcourir…"
            onFocus={() => setOuvert(true)}
            onChange={(e) => { setTerme(e.target.value); setChemin(null); setOuvert(true); }}
          />
          {chargement && <Loader size={14} className="kcorr-tourne" />}
        </div>
      )}

      {ouvert && !valeur && (
        <div className="kcat-liste">
          {niveaux.length > 0 && (
            <div className="kcat-fil">
              <button type="button" onClick={() => setChemin(null)}>Toutes</button>
              {niveaux.map((niveau, rang) => (
                <button
                  key={niveau}
                  type="button"
                  onClick={() => setChemin(niveaux.slice(0, rang + 1).join(' > '))}
                >
                  {niveau}
                </button>
              ))}
            </div>
          )}

          {tronque && (
            <p className="kcat-note">Trop de résultats : seuls les 50 premiers sont affichés.</p>
          )}

          {resultats.length === 0 && !chargement && (
            <p className="kcat-note">
              {terme && terme.length >= 2
                ? 'Aucune catégorie ne correspond.'
                : 'Taxonomie indisponible : elle est téléchargée chez Google et peut manquer un instant.'}
            </p>
          )}

          <ul>
            {resultats.map(categorie => (
              <li key={categorie.id}>
                <button type="button" className="kcat-choisir" onClick={() => retenir(categorie)}>
                  <span>{categorie.shortName || categorie.name}</span>
                  <span className="kcat-id">{categorie.id}</span>
                </button>
                {/* Un nœud intermédiaire se retient **aussi** : Google accepte une catégorie à
                    n'importe quel niveau, et « Beaux-Arts » vaut mieux que rien. */}
                {categorie.hasChildren && (
                  <button
                    type="button"
                    className="kcat-descendre"
                    onClick={() => { setTerme(''); setChemin(categorie.name); }}
                    aria-label={`Ouvrir ${categorie.shortName || categorie.name}`}
                  >
                    <ChevronRight size={14} />
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ChoixCategorieGoogle;
